import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { Store } from "@ngrx/store";
import moment from 'moment';
import { Subject, take, takeUntil } from "rxjs";
import { ApiService } from "../api.service";
import { AppState } from "../app.state";
import { AuthService } from "../auth/auth.service";
import { LocalizableComponent } from "../components/localizable/localizable.component";
import { EnvironmentService } from "../services/environment.service";
import { MapLoaderService } from "../services/map-loader.service";
import { StringService } from "../services/string.service";
import { ToastService } from "../services/toast.service";
import { LangSection, LangText } from "../types/language";
import { LocationProfile } from "../types/locationProfile";
import { OnPropertyData, TrailerAsset } from "../types/onPropertyData";
import { Role } from "../types/role";
import { Toast, ToastType } from "../types/Toast";

@Component({
  selector: 'on-property',
  templateUrl: './on-property.component.html',
  styleUrls: ['./on-property.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})

export class OnPropertyComponent extends LocalizableComponent implements OnInit, OnDestroy {

  locationOptions: LocationProfile[] = [];
  filteredLocationOptions: LocationProfile[] = [];
  currentLocation: LocationProfile = null;
  locationSearchTerm: string = '';
  trailerListIsLoading: boolean = true;
  iconClass: string = 'search icon';
  trailerSearch: string = "";
  displayedTrailerCountMessage: string = "";

  filteredTrailers: TrailerAsset[] = [];
  allTrailers: TrailerAsset[] = [];
  mapLoaded: boolean = false;
  selectedTrailer: TrailerAsset;
  locationLoading: boolean;
  assetData: OnPropertyData;
  private errorToastId = 'on-property-get-error';

  filterForm: UntypedFormGroup = this.formBuilder.group({
    'available': false,
    'empty': false
  });
  showFilters: boolean = false;
  filterActive: boolean = false;
  showSortOptions: boolean = false;
  sortedBy: TrailerListSort = TrailerListSort.Available;
  ascending: boolean = true;

  TrailerListSort = TrailerListSort;

  disconnect$: Subject<boolean> = new Subject<boolean>();

  constructor(private authService: AuthService, private route: ActivatedRoute, private router: Router, private store: Store<AppState>,
    private stringService: StringService, private apiService: ApiService, private mapLoaderService: MapLoaderService,
    private toastService: ToastService, private env: EnvironmentService, private formBuilder: UntypedFormBuilder) {
    super();
  }

  ngOnInit(): void {
    this.mapLoaderService.load()
      .then(_ => {
        this.mapLoaded = true;
        console.log('Bing maps loaded');
      });

    this.route.params
      .subscribe(params => {
        this.store
          .select(s => s.locationList)
          .pipe(take(1))
          .subscribe(state => {
            if (!(params.slicNumber && params.countryCode)) {
              let validLocations = state.locations.filter(l => this.hasLocationPermissions(l.slicName));
              if (validLocations?.length === 0) {
                this.router.navigateByUrl("/");
                return;
              }

              this.currentLocation = validLocations[0]?.profile;
              this.router.navigateByUrl(`trailer-reservation-system/onproperty/${this.currentLocation.countryCode}/${this.currentLocation.slicNumber}`);
              this.loadOnPropertyData();
            } else {
              let location = state.locations.find(loc => params.slicNumber == loc.slicNumber)?.profile;
              let validLocation = this.hasLocationPermissions(location?.slicName);
              if (!validLocation) {
                this.router.navigateByUrl("/");
                return;
              }

              this.currentLocation = location;
              this.loadOnPropertyData();
            }
          });
      });

    this.store.select(s => s.locationList)
      .pipe(take(1))
      .subscribe(locList => {
        this.locationOptions = locList.locations?.slice(1).map(l => {
          return l.profile;
        });

        this.filteredLocationOptions = [...this.locationOptions];
      });

      this.filterForm.valueChanges.pipe(takeUntil(this.disconnect$)).subscribe(formValue => {
        this.filterTrailers();
        this.filterActive = this.filterForm.get('available').value || this.filterForm.get('empty').value;
      });
      this.sortTrailers();
  }

  ngOnDestroy(): void {
    this.disconnect$.next(true);
    this.disconnect$.unsubscribe();
  }


  private loadOnPropertyData() {
    this.locationLoading = true;
    this.mapLoaded = false;
    this.apiService.getOnPropertyDetailsForLocation(this.currentLocation)
      .subscribe({
        next: assetData => {
          this.mapLoaderService.load()
            .then(_ => {
              this.mapLoaded = true;
              this.assetData = assetData;
              this.allTrailers = assetData?.assets?.trailers?.sort((a, b) => a.trailerNumber > b.trailerNumber ? 1 : -1);
              this.filteredTrailers = this.allTrailers;
              this.trailerListIsLoading = false;
              this.getFilterString();
              this.locationLoading = false;
            });
        },
        error: _ => {
          this.toastService.pushToast({
            id: this.errorToastId,
            text: this.localize(this.langSection.Toast, this.langText.FailedToRetrieveOnPropertyData),
            duration: 10
          });
          this.locationLoading = false;
        }
      });
  }

  onLocationSelect(locProfile: LocationProfile) {
    this.trailerListIsLoading = true;
    this.router.navigateByUrl(`/onproperty/${locProfile.countryCode}/${locProfile.slicNumber}`, { replaceUrl: true });
  }

  filterLocations() {
    const trimmedNormalizedTerm = this.stringService.normalizeString(this.locationSearchTerm.trim().toLowerCase());
    this.filteredLocationOptions = this.locationOptions.filter(lp =>
      this.stringService.normalizeString(lp.slicName.toLowerCase()).includes(trimmedNormalizedTerm) ||
      this.stringService.normalizeString(lp.crdSlicName.toLowerCase()).includes(trimmedNormalizedTerm) ||
      this.stringService.normalizeString(lp.address.toLowerCase()).includes(trimmedNormalizedTerm));
  }

  clearSearch() {
    this.locationSearchTerm = '';
    this.filterLocations();
  }

  searchTrailers() {
    this.iconClass = this.trailerSearch === "" ? 'search icon' : 'clear icon';
    this.filteredTrailers = this.allTrailers.filter(ts => ts.trailerNumber.includes(this.trailerSearch));
    this.getFilterString();
    this.filterTrailers();
  }

  clearTrailerSearch() {
    this.trailerSearch = "";
    this.iconClass = 'search icon';
    this.filteredTrailers = this.allTrailers;
    this.getFilterString();
  }

  filterTrailers() {
    if (!this.filterForm.get('available').value || !this.filterForm.get('empty').value)
      this.searchTrailers();
    if (this.filterForm.get('available').value)
      this.filteredTrailers = this.filteredTrailers.filter(ts => !ts.requestId);
    if (this.filterForm.get('empty').value)
      this.filteredTrailers = this.filteredTrailers.filter(ts => !ts.pieceCount && !ts.percentFull && !ts.palletCount);
    this.getFilterString();
    this.sortTrailers();
  }

  getFilterString() {
    if (this.filteredTrailers?.length > 0 && this.filteredTrailers?.length !== this.allTrailers?.length) {
      this.displayedTrailerCountMessage = `${this.localize(this.langSection.OnProperty, this.langText.Showing)} ${this.filteredTrailers.length} ${this.localize(this.langSection.OnProperty, this.langText.Of)} ${this.allTrailers.length} ${this.localize(this.langSection.OnProperty, this.langText.Trailers)}`;
    }
    else if (this.filteredTrailers?.length > 0 && this.filteredTrailers?.length === this.allTrailers?.length) {
      this.displayedTrailerCountMessage = `${this.localize(this.langSection.OnProperty, this.langText.Showing)} ${this.filteredTrailers.length} ${this.localize(this.langSection.OnProperty, this.langText.Trailers)}`;
    }
    else if (this.allTrailers?.length < 1) {
      this.displayedTrailerCountMessage = `${this.localize(this.langSection.OnProperty, this.langText.Showing)} 0 ${this.localize(this.langSection.OnProperty, this.langText.Trailers)}`;
    }
    else if (this.filteredTrailers?.length < 1 && this.allTrailers?.length > 0) {
      this.displayedTrailerCountMessage = `${this.localize(this.langSection.OnProperty, this.langText.Showing)} 0 ${this.localize(this.langSection.OnProperty, this.langText.Of)} ${this.allTrailers.length} ${this.localize(this.langSection.OnProperty, this.langText.Trailers)}`;
    }
    else {
      this.displayedTrailerCountMessage = `${this.localize(this.langSection.OnProperty, this.langText.Showing)} ${this.allTrailers?.length ?? 0} ${this.localize(this.langSection.OnProperty, this.langText.Trailers)}`;
    }
  }

  updateSelectedTrailer(trailer: TrailerAsset) {
    this.selectedTrailer = trailer;
  }

  exportTrailers() {
    const exportPayload = {
      countryCode: this.currentLocation.countryCode,
      slicNumber: this.currentLocation.slicNumber,
      slicAbbreviation: this.currentLocation.slicName,
      language: this.getLanguageCode()
    };


    const currentDateTime = moment(new Date());
    const exportFileName = `${this.localize(LangSection.OnProperty, LangText.TrailerListExportFileName)}_${currentDateTime.format('YYYYMMDD')}_${currentDateTime.format('HHmmss')}`

    this.apiService.exportTrailers(exportFileName, exportPayload).subscribe({
      next: () => {
        const successToast: Toast = { id: 'trailerExport', text: this.localize(LangSection.OnProperty, LangText.TrailerListExportSuccess), duration: 5000, toastType: ToastType.Success };
        this.toastService.pushToast(successToast);
      },
      error: () => {
        const errorToast: Toast = { id: 'trailerExport', text: this.localize(LangSection.OnProperty, LangText.TrailerListExportFailure), duration: 5000, toastType: ToastType.Error }
        this.toastService.pushToast(errorToast);
      }
    })
  }

  private hasLocationPermissions(slicName: string): boolean {
    return this.authService.hasLocation(slicName) && (this.authService.hasLocationRole(slicName, Role.ReadonlyUser) || this.authService.hasLocationRole(slicName, Role.User) || this.authService.hasLocationRole(slicName, Role.Admin))
  }

  onFilterClick() {
    this.showSortOptions = false;
    this.showFilters = !this.showFilters;
  }

  onSortClick() {
    this.showFilters = false;
    this.showSortOptions = !this.showSortOptions;
  }

  updateSort(sort: number) {
    if (sort === this.sortedBy) 
      this.ascending = !this.ascending;
    else {
      this.sortedBy = sort;
      this.ascending = true;
    }
    this.sortTrailers();
  }

  sortTrailers() {
    this.filteredTrailers = this.filteredTrailers.sort((a, b) => {
      let compare: number = 0;
      switch(this.sortedBy) {
        case TrailerListSort.Empty:
          compare = ((!!a.pieceCount || !!a.percentFull || !!a.palletCount) < (!!b.pieceCount || !!b.percentFull || !!b.palletCount)) ? -1 :
                    ((!!a.pieceCount || !!a.percentFull || !!a.palletCount) > (!!b.pieceCount || !!b.percentFull || !!b.palletCount)) ? 1 : 0;
          break;
        case TrailerListSort.Available:
        default:
          compare = (!!a.requestId < !!b.requestId) ? -1 : (!!a.requestId > !!b.requestId) ? 1 : 0;
          break;
      }

      if (!this.ascending) compare = 0 - compare;
      
      if (compare === 0) {
        compare = (a.trailerNumber < b.trailerNumber) ? -1 : (a.trailerNumber > b.trailerNumber) ? 1 : 0;
      }

      return compare;
    });
  }
}

export enum TrailerListSort {
  Available = 0,
  Empty = 1
}
